import React from "react";
import cx from "classnames";
import { Icon, BetOdds, StatusPill } from "library";
import type { IBetPickDetails, OddsFormat } from "types/BetTypes";
import OutcomeIcon from "sections/Betting/Event/OutcomeIcon";
import * as styles from "./BetPick.module.scss";
import { TitleIcon } from "components/TitleIcon";
import { useTitle } from "hooks/graphql/useTitles";
import type { PickemsContest } from "hooks/firestore/pickems/types";
import { Clamp } from "components/Clamp";
import { ReactComponent as CloseIcon } from "components/assets/close.svg";
import { useEvent } from "hooks/firestore/v2/betting/useEvent";
import { StreamButton } from "components/layout/StreamButton";
import { EventStatus, StatusEnum } from "components/layout/EventStatus";
import { isPast } from "date-fns";
import { OddsChanges } from "components/OddsChanges";
import { ReactComponent as ArrowDown } from "components/assets/flucs-down.svg";
import { ReactComponent as ArrowUp } from "components/assets/flucs-up.svg";

type BetPickProps = {
  selection: IBetPickDetails;
  oddsFormat: OddsFormat;
  doRemoveSelection: (x: string) => void;
  isConfirming?: boolean;
  canClose?: boolean;
  isSRM?: boolean;
  contest: PickemsContest;
  eventId?: string;
};

export const BetPick = (props: BetPickProps) => {
  const {
    selection,
    oddsFormat,
    doRemoveSelection,
    isConfirming,
    contest,
    eventId,
  } = props;

  const {
    outcomeId,
    eventName,
    selectionName,
    oddsChanged,
    freshOdds,
    lockedOdds,
    marketName,
    outcome,
    statusMessage,
  } = selection;

  const { event } = useEvent(eventId, "all");
  const hasStarted = event && isPast(event.scheduledStartTime);
  const isSuspended = event?.status === StatusEnum.SUSPENDED && !hasStarted;
  if (event?.status === StatusEnum.SUSPENDED && hasStarted) {
    event.status = StatusEnum.LIVE;
  }
  const isCustom = outcome?.type && outcome?.type === "CUSTOM";
  const title = useTitle(event?.sport.toLowerCase() ?? "");

  return (
    <OddsChanges
      odds={
        oddsChanged && !isConfirming ? Number(freshOdds) : Number(lockedOdds)
      }
      className={cx(styles.pickContent)}
      animationLength={oddsChanged ? null : 3000}
    >
      <div className={styles.header}>
        <div className={styles.icon}>
          <TitleIcon title={title} svg />
        </div>
        <div className={styles.title}>
          <div className={styles.selectionName}>
            {isCustom ? (
              <Icon type="ribbon" className={styles.pickIcon} />
            ) : (
              <OutcomeIcon
                className={styles.outcomeIcon}
                outcome={outcome}
                competitor={outcome?.competitor}
                size={20}
              />
            )}
            {selectionName}
          </div>
          <div className={styles.eventNamePickSlip}>{eventName}</div>
          <div className={styles.oddsWrapper}>
            {selection.isSP ? (
              <span>{marketName}</span>
            ) : (
              <>
                {oddsChanged && (
                  <BetOdds
                    className={cx(styles.oldOdds, {
                      [styles.up]: Number(freshOdds) > Number(lockedOdds),
                      [styles.down]: Number(freshOdds) < Number(lockedOdds),
                    })}
                    base={lockedOdds}
                    format={oddsFormat}
                  />
                )}
                <BetOdds
                  prefix={
                    freshOdds || lockedOdds ? `${marketName || ""} @` : ""
                  }
                  base={oddsChanged && !isConfirming ? freshOdds : lockedOdds}
                  format={oddsFormat}
                  settlementOdds={outcome?.settlementOdds}
                />
              </>
            )}
            <div className={styles.arrowUp}>
              <ArrowUp />
            </div>
            <div className={styles.arrowDown}>
              <ArrowDown />
            </div>
            {statusMessage && statusMessage.text === "LIVE" ? (
              <StatusPill color={statusMessage.color} className={styles.starts}>
                {statusMessage.text}
              </StatusPill>
            ) : null}
          </div>
        </div>
        <div className={styles.clamp}>
          <Clamp
            variant={`grey`}
            onClick={() => doRemoveSelection(outcomeId)}
            className={cx(styles.close)}
          >
            <CloseIcon />
          </Clamp>

          <div className={styles.statusWithStream}>
            {event && ["ACTIVE", "LIVE"].includes(event.status) && (
              <StreamButton
                title={event.eventName}
                sport={event.sport}
                url={event.streamUri ?? ""}
                startTime={event.nextBetStop || event.scheduledStartTime}
                status={event.status}
                eventId={event.eventId}
                isPlaceholder={event.attributes?.streamExpected}
                hideIfNotThirtyMinutesBeforeStart
              />
            )}
            {isSuspended ? (
              <Clamp variant="disabled">
                <Icon type="lock" className={styles.lockIcon} />
              </Clamp>
            ) : (
              event && (
                <EventStatus
                  status={event.status}
                  scheduledStartTime={
                    event.nextBetStop || event.scheduledStartTime
                  }
                />
              )
            )}
          </div>
        </div>
      </div>
    </OddsChanges>
  );
};

export default BetPick;
